<script setup>
// T-204 — causa raiz × criticidade × sentimento, numa tabela só.
//
// COMO SE LÊ: cada linha é uma causa raiz confirmada no encerramento, cada coluna é um nível da
// matriz do clube, e dentro da célula a barrinha mostra como aquele grupo se dividiu entre
// negativo, neutro e positivo. A pergunta que a tabela responde não é "quantos críticos temos",
// é "os críticos que temos são críticos mesmo".
//
// A barra de sentimento é o terceiro eixo e é ela que separa os dois desfechos: célula vermelha
// cheia de sentimento negativo é operação com problema de verdade; célula vermelha com
// sentimento neutro é classificação exagerando.
//
// As linhas vêm ordenadas do servidor pelo nível mais grave, então a leitura é de cima para
// baixo e a primeira linha é onde olhar primeiro.
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import subDays from 'date-fns/subDays';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import CruzamentoAPI from 'dashboard/api/fandata/cruzamento';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import BaixarRelatorio from './components/BaixarRelatorio.vue';
import AtalhoAnalitico from './components/AtalhoAnalitico.vue';
import CortinaConversas from './components/CortinaConversas.vue';
import FiltrosRelatorio from './components/FiltrosRelatorio.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

const { t } = useI18n();

const dados = ref(null);
const carregando = ref(true);
const erro = ref(false);
const intervalo = ref([subDays(new Date(), 6), new Date()]);
const tipoDeIntervalo = ref(DATE_RANGE_TYPES.LAST_7_DAYS);
// O período viaja em timestamp unix, que é o que o FandataPeriodo lê no servidor. Mandar ISO
// daqui faz `to_i` virar o ANO e a janela cair em 1970, sem erro nenhum na tela.
const desde = ref(getUnixStartOfDay(subDays(new Date(), 6)));
const ate = ref(getUnixEndOfDay(new Date()));
// T-252: canal e lado. Nulo e 'todos' são o padrão: quem abre a tela vê a operação inteira.
const canalFiltrado = ref(null);
const ladoFiltrado = ref('todos');

const SENT_CORES = {
  negativo: 'bg-n-ruby-9',
  neutro: 'bg-n-amber-9',
  positivo: 'bg-n-teal-9',
};

const carregar = async () => {
  carregando.value = true;
  erro.value = false;
  try {
    const { data } = await CruzamentoAPI.get({
      since: desde.value,
      until: ate.value,
      inboxId: canalFiltrado.value,
      lado: ladoFiltrado.value === 'todos' ? undefined : ladoFiltrado.value,
    });
    dados.value = data;
  } catch (e) {
    erro.value = true;
  } finally {
    carregando.value = false;
  }
};

onMounted(carregar);

const aoTrocarPeriodo = valor => {
  const [inicio, fim, tipo] = valor;
  intervalo.value = [inicio, fim];
  tipoDeIntervalo.value = tipo || DATE_RANGE_TYPES.CUSTOM_RANGE;
  desde.value = getUnixStartOfDay(inicio);
  ate.value = getUnixEndOfDay(fim);
  carregar();
};

const temDados = computed(() => (dados.value?.linhas || []).length > 0);

// T-289 — o bloco "Panorama", que era uma aba só dele.
//
// A Tamara pediu a aba fora na reunião de 01/10 ("senão são muitas telas para ter as mesmas
// informações") e os números para cá ("esses grandes números aqui irem lá para o cruzamento").
// O nome Panorama veio com eles, dentro da tela: "e eu posso chamar de panorama lá dentro".
//
// Vêm na MESMA resposta da tabela, de propósito. Antes eram outro endpoint, com outro filtro de
// período e de canal, e nada garantia que os dois concordassem: dava para ler "180 em
// atendimento" num filtro e a tabela de outro. Agora é uma consulta e um filtro só.
//
// E são FOTO DO PERÍODO, não tempo real (decisão do Douglas em 02/10). Na aba antiga eles se
// atualizavam de 30 em 30 segundos, o que obrigava a ligar e desligar o relógio ao trocar de aba
// para a consulta não rodar à toa. Ninguém pediu tempo real aqui, e quem quer tem o Funil.
const CONTADORES = ['TOTAL', 'OPEN', 'PENDING', 'RESOLVED', 'HANDOFF'];
const CHAVES = {
  TOTAL: 'total',
  OPEN: 'open',
  PENDING: 'pending',
  RESOLVED: 'resolved',
  HANDOFF: 'bot_handoff',
};
// O destaque é o mesmo da aba antiga: a cor diz o estado, não a importância.
const CORES = {
  TOTAL: 'text-n-slate-12',
  OPEN: 'text-n-blue-11',
  PENDING: 'text-n-amber-11',
  RESOLVED: 'text-n-teal-11',
  HANDOFF: 'text-n-iris-11',
};

const contadores = computed(() =>
  CONTADORES.map(key => ({
    key,
    label: t(`FAROL.COUNTERS.${key}`),
    // Zero é resposta: quando não há conversa no período o cartão diz 0, e não fica vazio como
    // se o número não tivesse carregado.
    valor: dados.value?.contadores?.[CHAVES[key]] ?? 0,
    cor: CORES[key],
  }))
);

// Rótulo da linha: causa nula é a ausência de tabulação, e ela ganha nome próprio na tela em
// vez de aparecer como célula vazia, que seria lida como erro do relatório.
const rotuloDaCausa = causa => causa || t('FAROL.CRUZAMENTO.SEM_TABULACAO');

// O nome do protocolo vem como etiqueta ("protocolo-vermelho", "tolerância-zero"), que é o
// mesmo texto usado no resto do sistema. Numa tabela de seis colunas ele empurra a largura toda
// para o cabeçalho e sobra pouco para o que importa, que são os números. Quebrado no hífen, a
// coluna encolhe e o título continua sendo a mesma palavra.
const rotuloDoProtocolo = protocolo =>
  (protocolo.label || t('FAROL.CRUZAMENTO.SEM_PROTOCOLO')).replace(/[-\s]+/g, '\n');

// Largura de cada pedaço da barra, em porcentagem do total da célula.
const fatia = (celula, sentimento) => {
  if (!celula.total) return 0;
  return Math.round((celula.sentimentos[sentimento] / celula.total) * 100);
};

// Clicar numa linha abre as conversas daquela causa raiz; clicar numa célula abre só o recorte
// daquele protocolo. É o mesmo padrão de drill das outras telas de relatório, pelo mesmo
// componente, para a cortina se comportar igual em todo lugar.
//
// A lista sai do MESMO escopo de onde o número saiu: se divergisse da tabela, a tela passaria a
// mentir justamente no clique em que a pessoa foi conferir.
const cortina = reactive({
  aberta: false,
  titulo: '',
  subtitulo: '',
  carregando: false,
  conversas: [],
  total: null,
});

const fecharCortina = () => {
  cortina.aberta = false;
};

const abrirConversas = async ({ causa, protocolo, titulo, subtitulo }) => {
  Object.assign(cortina, {
    aberta: true,
    titulo,
    subtitulo,
    carregando: true,
    conversas: [],
    total: null,
  });
  try {
    const { data } = await CruzamentoAPI.conversas({
      causa,
      protocolo,
      since: desde.value,
      until: ate.value,
      inboxId: canalFiltrado.value,
      lado: ladoFiltrado.value === 'todos' ? undefined : ladoFiltrado.value,
    });
    cortina.conversas = data.conversas || [];
    cortina.total = data.total ?? null;
  } catch (e) {
    cortina.aberta = false;
  } finally {
    cortina.carregando = false;
  }
};

const abrirLinha = linha =>
  abrirConversas({
    causa: linha.causa,
    titulo: rotuloDaCausa(linha.causa),
    subtitulo: t('FAROL.CRUZAMENTO.CORTINA_LINHA', { total: linha.total }),
  });

const abrirCelula = (linha, protocolo) => {
  const celula = linha.celulas[protocolo.value];
  if (!celula || !celula.total) return abrirLinha(linha);
  return abrirConversas({
    causa: linha.causa,
    protocolo: protocolo.value,
    titulo: rotuloDaCausa(linha.causa),
    subtitulo: t('FAROL.CRUZAMENTO.CORTINA_CELULA', {
      protocolo: protocolo.label || t('FAROL.CRUZAMENTO.SEM_PROTOCOLO'),
      total: celula.total,
    }),
  });
};

const resumoDaCelula = celula =>
  t('FAROL.CRUZAMENTO.CELULA_TITULO', {
    total: celula.total,
    negativo: celula.sentimentos.negativo,
    neutro: celula.sentimentos.neutro,
    positivo: celula.sentimentos.positivo,
  });
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-start flex-wrap gap-3">
      <WootDatePicker
        v-model:date-range="intervalo"
        v-model:range-type="tipoDeIntervalo"
        @date-range-changed="aoTrocarPeriodo"
      />
      <!-- T-260: baixa exatamente o que esta tela está mostrando. -->
      <!-- T-252: canal e lado. Trocar qualquer um recarrega a tela. -->
      <FiltrosRelatorio
        v-model:inbox-id="canalFiltrado"
        v-model:lado="ladoFiltrado"
        @update:inbox-id="carregar"
        @update:lado="carregar"
      />
      <BaixarRelatorio
        nome="cruzamento"
        :dados="dados"
        :since="desde"
        :until="ate"
      />
      <!-- T-260 (leitura A): o caminho para a quebra completa, que já existe numa tela só. -->
      <AtalhoAnalitico />
    </div>

    <!-- T-289: o Panorama, que era uma aba. Fica antes da tabela porque a leitura é essa: o
         volume do período primeiro, e depois onde ele está concentrado. -->
    <section v-if="!carregando && !erro">
      <h3 class="mb-2 text-base font-medium text-n-slate-12">
        {{ $t('FAROL.CRUZAMENTO.PANORAMA') }}
      </h3>
      <div class="grid grid-cols-2 gap-3 md:grid-cols-5">
        <div
          v-for="c in contadores"
          :key="c.key"
          class="flex flex-col gap-1 p-4 rounded-xl border border-n-weak bg-n-solid-1"
        >
          <span class="text-xs font-medium text-n-slate-11">{{ c.label }}</span>
          <span class="text-2xl font-semibold" :class="c.cor">{{
            c.valor
          }}</span>
        </div>
      </div>
    </section>

    <p class="text-sm text-n-slate-11">
      {{ $t('FAROL.CRUZAMENTO.COMO_LER') }}
      {{ $t('FAROL.CRUZAMENTO.DICA_CLIQUE') }}
    </p>

    <div v-if="carregando" class="py-8 text-sm text-center text-n-slate-11">
      {{ $t('FAROL.CRUZAMENTO.CARREGANDO') }}
    </div>

    <div v-else-if="erro" class="py-8 text-sm text-center text-n-ruby-11">
      {{ $t('FAROL.CRUZAMENTO.ERRO') }}
    </div>

    <div v-else-if="!temDados" class="py-8 text-sm text-center text-n-slate-11">
      {{ $t('FAROL.CRUZAMENTO.VAZIO') }}
    </div>

    <template v-else>
      <!-- Tabela larga rola sozinha: a página não pode rolar de lado por causa dela. -->
      <div class="overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="text-left">
              <th class="py-2 pr-4 font-medium text-n-slate-11">
                {{ $t('FAROL.CRUZAMENTO.CAUSA') }}
              </th>
              <th
                v-for="p in dados.protocolos"
                :key="p.value"
                class="px-2 py-2 font-medium text-center align-bottom text-n-slate-11 whitespace-pre-line leading-tight"
              >
                {{ rotuloDoProtocolo(p) }}
              </th>
              <th class="py-2 pl-2 font-medium text-right text-n-slate-11">
                {{ $t('FAROL.CRUZAMENTO.TOTAL') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="linha in dados.linhas"
              :key="linha.causa || 'sem'"
              class="border-t cursor-pointer border-n-weak hover:bg-n-alpha-1"
              @click="abrirLinha(linha)"
            >
              <td
                class="py-2 pr-4 align-middle text-n-slate-12"
                :class="{ 'italic text-n-slate-11': !linha.causa }"
              >
                {{ rotuloDaCausa(linha.causa) }}
              </td>
              <td
                v-for="p in dados.protocolos"
                :key="p.value"
                class="px-2 py-2 align-middle"
                @click.stop="abrirCelula(linha, p)"
              >
                <div
                  v-if="linha.celulas[p.value].total"
                  class="flex flex-col items-center gap-1"
                  :title="resumoDaCelula(linha.celulas[p.value])"
                >
                  <span class="font-medium tabular-nums text-n-slate-12">
                    {{ linha.celulas[p.value].total }}
                  </span>
                  <span class="flex w-12 h-1.5 overflow-hidden rounded-full">
                    <span
                      v-for="s in dados.sentimentos"
                      :key="s"
                      :class="SENT_CORES[s]"
                      :style="{ width: `${fatia(linha.celulas[p.value], s)}%` }"
                    />
                  </span>
                </div>
                <span v-else class="block text-center text-n-slate-9">·</span>
              </td>
              <td
                class="py-2 pl-2 font-medium text-right align-middle tabular-nums text-n-slate-12"
              >
                {{ linha.total }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex flex-wrap items-center gap-4 text-xs text-n-slate-11">
        <span v-for="s in dados.sentimentos" :key="s" class="flex items-center gap-1.5">
          <span class="size-2 rounded-full" :class="SENT_CORES[s]" />
          {{ $t(`FAROL.CRUZAMENTO.SENTIMENTO.${s}`) }}
        </span>
        <span v-if="dados.causas > dados.mostradas">
          {{
            $t('FAROL.CRUZAMENTO.CORTADAS', {
              mostradas: dados.mostradas,
              causas: dados.causas,
              total: dados.total,
            })
          }}
        </span>
      </div>
    </template>
  </div>

  <CortinaConversas
    v-if="cortina.aberta"
    :titulo="cortina.titulo"
    :subtitulo="cortina.subtitulo"
    :conversas="cortina.conversas"
    :carregando="cortina.carregando"
    :total="cortina.total"
    @fechar="fecharCortina"
  >
    <template #direita="{ linha }">
      <span class="text-xs whitespace-nowrap text-n-slate-11">
        {{ linha.protocolo === '__sem_protocolo__' ? '' : linha.protocolo }}
      </span>
    </template>
  </CortinaConversas>
</template>
